import { useEffect, useState } from 'react';
import {
  FormControl,
  FormLabel,
  VStack,
  Box,
  Text,
  Button,
  useToast,
  Input,
} from '@chakra-ui/react';

import StrongModal from '../common/StrongModal';
import StrongInput from '../common/StrongInput';
import StrongTextarea from '../common/StrongTextarea';

interface UploadVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    title: string,
    description: string,
    videoFile: File | null
  ) => void;
  isLoading: boolean;
}

const UploadVideoModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}: UploadVideoModalProps) => {
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setDescription('');
      setVideoFile(null);
    }
  }, [isOpen]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setVideoFile(file);
    } else {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a valid video file.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <StrongModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={() => onSubmit(title, description, videoFile)}
      title='Upload Video'
      isSubmitDisabled={!title || !videoFile}
      isLoading={isLoading}
      body={
        <VStack spacing={4} align='stretch'>
          <FormControl>
            <FormLabel color='gray.600'>Title</FormLabel>
            <StrongInput
              placeholder='Enter video title'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel color='gray.600'>Description</FormLabel>
            <StrongTextarea
              placeholder='Enter video description'
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel color='gray.600'>Video File</FormLabel>
            <Box
              border='2px dashed'
              borderColor='gray.300'
              borderRadius='md'
              p={4}
              textAlign='center'
              bg='gray.50'
            >
              <Text color='gray.600'>Drag and drop your video file here</Text>
              <Text color='gray.400' fontSize='sm'>
                (Only video files are accepted)
              </Text>
              <Button
                mt={2}
                size='sm'
                colorScheme='primary'
                onClick={() =>
                  document.getElementById('videoFileInput')?.click()
                }
              >
                Select File
              </Button>
              <Input
                id='videoFileInput'
                type='file'
                accept='video/*'
                hidden
                onChange={handleFileChange}
              />
              {videoFile && (
                <Text mt={2} fontSize='sm' color='primary.500'>
                  {videoFile.name}
                </Text>
              )}
            </Box>
          </FormControl>
        </VStack>
      }
    />
  );
};

export default UploadVideoModal;
